import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { Button } from '@common/components';
import Breadcrumbs from '@common/components/Breadcrumbs';
import Header from '@common/components/Header';
import { fetchWidgetData } from '@api/dataSource';
import { convertToWidgetData } from '@common/utils/widgetDataTransform';
import { fetchStatusColorMap } from '@api/statuses';
import { applySeverityColors } from '@common/utils/severityColors';
import { PREVIEW_ROW_LIMIT } from '@reports/utils/dashboard/constants';
import { loadMetricValuesDataForPreview } from '@reports/utils/dashboard/loadMetricValuesDataForPreview';
import { combineSeriesData, getTableYAxisIndex } from '@reports/utils/dashboard/combineSeriesData';
import { fetchCustomQueryLineData } from '@reports/utils/dashboard/fetchCustomQueryLineData';
import { normalizeWidgets, calculateGridLayouts, GRID_CONFIG } from '@reports/utils/dashboard/layoutUtils';
import { useReportDependencies } from '../../hooks/useReportDependencies';
import WidgetRenderer from '../Widgets/WidgetRenderer';
import WidgetWizard from './WidgetWizard';
import FilterMultiSelect from './FilterMultiSelect';
import ReportDetailsModal from './ReportDetailsModal';
import ReportDependencyWarning from './ReportDependencyWarning';
import GridOverlay from './GridOverlay';
import * as styles from './DashboardEditor.module.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

function DashboardEditor({ dashboard, isCreating, saving, saveSuccess, error, onSave, onCancel, entityType = 'Report' }) {
  const { t } = useTranslation();

  // Fetch dependencies for existing reports (not when creating new)
  const { savedReportsCount, scheduledReportsCount, savedReports, scheduledReports } = useReportDependencies(
    dashboard?.id,
    {
      enabled: !isCreating && !!dashboard?.id,
    },
  );

  const [dashboardData, setDashboardData] = useState({
    name: '',
    description: '',
    type: 2, // Default to report
    status: 0, // Default to draft
    creator: null,
    widgets: [],
    requiredFilters: [], // Empty array - no filters required by default
  });
  const [showWidgetEditor, setShowWidgetEditor] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [layouts, setLayouts] = useState({ lg: [] });
  const [widgetsWithPreviewData, setWidgetsWithPreviewData] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewTrigger, setPreviewTrigger] = useState(0); // Counter to force preview reload
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState(null); // 'save' or 'publish'
  const gridRef = useRef(null);

  // Close modal when save completes successfully
  useEffect(() => {
    if (saveSuccess && showDetailsModal) {
      setShowDetailsModal(false);
      setPendingSaveAction(null);
    }
  }, [saveSuccess, showDetailsModal]);

  useEffect(() => {
    if (dashboard) {
      // In EDIT mode, don't load data - just show widget placeholders for performance
      // Data will only be loaded in VIEW mode
      const widgetsWithoutData = dashboard.widgets || [];

      // Normalize widgets using shared utility for consistency with Viewer
      const widgetsWithLayout = normalizeWidgets(widgetsWithoutData).map((w) => ({
        ...w,
        data: 'EDIT_MODE_PLACEHOLDER',
      }));

      setDashboardData({
        name: dashboard.name || '',
        description: dashboard.description || '',
        type: dashboard.type,
        status: dashboard.status,
        creator: dashboard.creator,
        widgets: widgetsWithLayout,
        requiredFilters: dashboard.requiredFilters || [], // Use from API or empty array
      });

      // Set up layouts using shared utility
      if (widgetsWithLayout.length > 0) {
        setLayouts(calculateGridLayouts(widgetsWithLayout));
      }

      // Trigger preview load immediately instead of waiting for next render cycle
      // Use setTimeout 0 to ensure state has been set first
      setTimeout(() => {
        setPreviewTrigger((prev) => prev + 1);
      }, 0);
    }
  }, [dashboard]);

  const handleRequiredFiltersChange = (newRequiredFilters) => {
    setDashboardData((prev) => ({
      ...prev,
      requiredFilters: newRequiredFilters,
    }));
  };

  const handleAddWidget = () => {
    setEditingWidget(null);
    setShowWidgetEditor(true);
  };

  const handleEditWidget = (widget) => {
    // Always get the latest widget data from dashboardData (source of truth)
    // The widget passed might have preview data, but we want the config
    const latestWidget = dashboardData.widgets.find((w) => w.id === widget.id);
    setEditingWidget(latestWidget || widget);
    setShowWidgetEditor(true);
  };

  const handleDeleteWidget = (widgetId) => {
    // Note: Using native confirm dialog for simplicity
    // In production, consider replacing with a proper modal component
    // eslint-disable-next-line no-alert
    if (window.confirm(t('dashboard.deleteWidget'))) {
      setDashboardData((prev) => ({
        ...prev,
        widgets: prev.widgets.filter((w) => w.id !== widgetId),
      }));

      setLayouts((prev) => ({
        lg: prev.lg.filter((l) => l.i !== String(widgetId)),
      }));
    }
  };

  /**
   * Checks if a position collides with any existing widget
   * @param {number} x - X position to check
   * @param {number} y - Y position to check
   * @param {number} w - Width of the widget
   * @param {number} h - Height of the widget
   * @param {Array} existingLayouts - Existing widget layouts
   * @returns {boolean} - True if position collides with an existing widget
   */
  const doesCollide = (x, y, w, h, existingLayouts) =>
    existingLayouts.some((layout) => {
      const layoutRight = layout.x + (layout.w || 1);
      const layoutBottom = layout.y + (layout.h || 1);
      const newRight = x + w;
      const newBottom = y + h;

      // Check for overlap (not collision if one is completely to the side/above/below)
      const horizontalOverlap = x < layoutRight && newRight > layout.x;
      const verticalOverlap = y < layoutBottom && newBottom > layout.y;

      return horizontalOverlap && verticalOverlap;
    });

  /**
   * Finds the next available position for a new widget using boundary-based scanning.
   * Instead of checking every cell, only checks positions at widget boundaries
   * (where widgets start/end) which are the only meaningful candidate positions.
   *
   * Default widget: 12 columns × 6 rows (50% width)
   * Text widget: 6 columns × 3 rows (25% width, smaller)
   * Grid: 24 columns
   *
   * Complexity: O(n²) where n = number of widgets, instead of O(cols × rows)
   * MON-17145
   *
   * @param {Array} existingLayouts - Array of existing widget layouts
   * @param {string|null} widgetType - Type of widget ('text' for smaller size)
   * @returns {Object} Position object with x, y, w, h
   */
  const findNextWidgetPosition = (existingLayouts, widgetType = null) => {
    const GRID_COLS = 24;

    // Text widgets use smaller default size
    const isTextWidget = widgetType === 'text';
    const DEFAULT_W = isTextWidget ? 6 : 12;
    const DEFAULT_H = isTextWidget ? 3 : 6;

    if (!existingLayouts || existingLayouts.length === 0) {
      return { x: 0, y: 0, w: DEFAULT_W, h: DEFAULT_H };
    }

    // Collect candidate X positions: 0, and right edge of each widget
    const candidateXs = new Set([0]);
    existingLayouts.forEach((layout) => {
      const rightEdge = layout.x + (layout.w || 1);
      if (rightEdge <= GRID_COLS - DEFAULT_W) {
        candidateXs.add(rightEdge);
      }
    });

    // Collect candidate Y positions: 0, and bottom edge of each widget
    const candidateYs = new Set([0]);
    existingLayouts.forEach((layout) => {
      candidateYs.add(layout.y + (layout.h || 1));
    });

    // Sort candidates for top-left priority
    const sortedXs = Array.from(candidateXs).sort((a, b) => a - b);
    const sortedYs = Array.from(candidateYs).sort((a, b) => a - b);

    // Build all candidate positions sorted by (y, x) for top-left priority
    const candidates = sortedYs.flatMap((y) => sortedXs.map((x) => ({ x, y })));

    // Find first non-colliding position
    const validPosition = candidates.find(
      ({ x, y }) => !doesCollide(x, y, DEFAULT_W, DEFAULT_H, existingLayouts),
    );

    if (validPosition) {
      return { ...validPosition, w: DEFAULT_W, h: DEFAULT_H };
    }

    // No space found, add to a new row at the bottom
    const maxY = Math.max(...existingLayouts.map((l) => l.y + (l.h || 1)), 0);
    return { x: 0, y: maxY, w: DEFAULT_W, h: DEFAULT_H };
  };

  const handleSaveWidget = (widgetData) => {
    if (editingWidget) {
      // Update existing widget
      const updatedWidget = {
        ...widgetData,
        id: editingWidget.id,
        layout: editingWidget.layout,
        // Don't set data here - let loadPreviewData fetch it
      };

      setDashboardData((prev) => ({
        ...prev,
        widgets: prev.widgets.map((w) => (w.id === editingWidget.id ? updatedWidget : w)),
      }));
    } else {
      // Find next available position (pass widget type for size-specific defaults)
      const position = findNextWidgetPosition(layouts.lg, widgetData.type);

      // Add new widget with proper positioning
      const newWidget = {
        ...widgetData,
        id: `widget-${Date.now()}`,
        layout: position,
        // Don't set data here - let loadPreviewData fetch it
      };

      setDashboardData((prev) => ({
        ...prev,
        widgets: [...prev.widgets, newWidget],
      }));

      setLayouts((prev) => ({
        lg: [
          ...prev.lg,
          {
            i: String(newWidget.id),
            x: position.x,
            y: position.y,
            w: position.w,
            h: position.h,
            minW: 1,
            minH: 1,
          },
        ],
      }));
    }

    setShowWidgetEditor(false);
    setEditingWidget(null);
  };

  const handleLayoutChange = (layout) => {
    // Update layouts
    setLayouts({ lg: layout });

    // Update widget layouts in dashboard data
    setDashboardData((prev) => ({
      ...prev,
      widgets: prev.widgets.map((widget) => {
        const layoutItem = layout.find((l) => l.i === String(widget.id));
        if (layoutItem) {
          return {
            ...widget,
            layout: {
              x: layoutItem.x,
              y: layoutItem.y,
              w: layoutItem.w,
              h: layoutItem.h,
            },
          };
        }
        return widget;
      }),
    }));
  };

  const handleSave = (publish = false, skipModalCheck = false, updatedDetails = null) => {
    // For new reports, if name is empty, show modal first (unless skipModalCheck is true)
    if (!skipModalCheck && isCreating && !dashboardData.name.trim()) {
      setPendingSaveAction(publish ? 'publish' : 'save');
      setShowDetailsModal(true);
      return;
    }

    // Use updated details if provided (from modal), otherwise use current state
    const nameToSave = updatedDetails?.name ?? dashboardData.name;
    const descriptionToSave = updatedDetails?.description ?? dashboardData.description;

    // Strip out widget data and client-generated IDs before saving
    const dashboardToSave = {
      ...dashboardData,
      name: nameToSave,
      description: descriptionToSave,
      widgets: dashboardData.widgets.map((widget) => {
        // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
        const { data, id, ...widgetWithoutData } = widget;

        // Only include database-assigned numeric IDs, not client-generated string IDs
        const widgetToSave = widgetWithoutData;
        if (typeof id === 'number') {
          widgetToSave.id = id;
        }

        return widgetToSave;
      }),
    };

    // If publish is true, set status to 1 (published)
    if (publish) {
      dashboardToSave.status = 1;
    }

    onSave(dashboardToSave);
  };

  const handleSaveAndPublish = () => {
    handleSave(true);
  };

  const handleDetailsModalSave = (details) => {
    const publish = pendingSaveAction === 'publish';
    setDashboardData((prev) => ({
      ...prev,
      name: details.name,
      description: details.description,
    }));

    // Don't close modal here - let useEffect close it when save succeeds
    // Pass the updated details directly to handleSave to avoid state timing issues
    handleSave(publish, true, details); // skipModalCheck = true, pass updated details
  };

  const handleDetailsModalCancel = () => {
    setShowDetailsModal(false);
    setPendingSaveAction(null);
  };

  const handleEditName = () => {
    setPendingSaveAction(null);
    setShowDetailsModal(true);
  };

  // Convert API response to widget data format
  // The data comes pre-aggregated from the server thanks to query optimization
  // Using shared utility function for consistent transformation

  // Load preview data for all widgets (with row limit)
  const loadPreviewData = useCallback(async () => {
    setIsLoadingPreview(true);
    try {
      if (dashboardData && dashboardData.widgets && dashboardData.widgets.length > 0) {
        const [widgetsWithDataRaw, statusColorMap] = await Promise.all([
          Promise.all(dashboardData.widgets.map(async (widget) => {
            const widgetWithData = { ...widget };

            // Skip data loading for table widgets in preview mode - they handle their own pagination
            if (widget.type === 'table' || widget.type === 'table-2d') {
              widgetWithData.data = null;
              widgetWithData.previewLimit = PREVIEW_ROW_LIMIT;
              return widgetWithData;
            }

            // Handle timeseries widget - uses /timeseries/sample endpoint
            if (widget.type === 'timeseries') {
              try {
                widgetWithData.data = await loadMetricValuesDataForPreview(widget, 3, {
                  format: 'timeseries',
                  pointsLimit: PREVIEW_ROW_LIMIT,
                });
              } catch {
                widgetWithData.data = null;
              }
              return widgetWithData;
            }

            // Get data sources from new structure
            const editorDataSources = widget.config?.dataSources || [];
            const editorHasCustomQuery = editorDataSources.some((ds) => ds.type === 'custom_query');
            const editorTableSources = editorDataSources.filter((ds) => ds.type === 'table');

            // Handle line chart with multiple data sources
            if (widget.type === 'line' && editorDataSources.length > 0) {
              try {
                const allSeriesData = [];
                const metricValuesSource = editorDataSources.find((ds) => ds.type === 'metric_values');

                // 1. Fetch metric_values data if selected
                // Config fields (groupBy, aggregateLiteral, aggregateFunction) are inside metricValuesSource.config
                if (metricValuesSource) {
                  const metricData = await loadMetricValuesDataForPreview(widget, 3);
                  if (metricData?.series) {
                    allSeriesData.push(...metricData.series.map((s, idx) => ({ 
                      ...s, 
                      yAxisIndex: 0,
                      categories: idx === 0 ? metricData.categories : undefined,
                      isMetricValues: true,
                    })));
                  }
                }

                // 2. Fetch table data for each table source (in parallel)
                // Config fields (timeColumn, yAxisField, groupBy) are inside tableSource.config
                const tableDataPromises = editorTableSources.map(async (tableSource, sourceIndex) => {
                  try {
                    // Build config with dataSource-specific fields inside dataSources array
                    const tableConfig = {
                      dataSources: [{
                        id: tableSource.id,
                        type: 'table',
                        config: tableSource.config || {},
                      }],
                      displayName: widget.config?.displayName,
                    };
                    
                    const response = await fetchWidgetData(
                      'line',
                      tableConfig,
                      {},
                      true,
                      PREVIEW_ROW_LIMIT,
                    );
                    
                    if (response.status === 'success' && response.data?.rows?.length) {
                      const tableData = convertToWidgetData(response.data, 'line', tableConfig, t('dashboard.widgets.noData'));
                      if (tableData) {
                        const yAxisIndex = getTableYAxisIndex(!!metricValuesSource, sourceIndex);
                        if (tableData.series) {
                          return tableData.series.map((s, idx) => ({
                            ...s,
                            name: `${tableSource.id}: ${s.name}`,
                            categories: idx === 0 ? tableData.categories : undefined,
                            yAxisIndex,
                          }));
                        }
                        if (tableData.categories && tableData.values) {
                          return [{
                            name: tableSource.id,
                            data: tableData.values,
                            categories: tableData.categories,
                            yAxisIndex,
                          }];
                        }
                      }
                    }
                    return [];
                  } catch {
                    return [];
                  }
                });
                
                const tableResults = await Promise.all(tableDataPromises);
                tableResults.forEach((series) => allSeriesData.push(...series));

                // 3. Fetch custom query data for line chart if selected
                const editorCustomQuerySource = editorDataSources.find((ds) => ds.type === 'custom_query');
                const customQuerySeries = await fetchCustomQueryLineData({
                  customQuerySource: editorCustomQuerySource,
                  widgetConfig: widget.config,
                  convertFn: (data, type, cfg) => convertToWidgetData(data, type, cfg, t('dashboard.widgets.noData')),
                  hasMetricValues: !!metricValuesSource,
                  tableSourceCount: editorTableSources.length,
                  previewRowLimit: PREVIEW_ROW_LIMIT,
                });
                customQuerySeries.forEach((s) => allSeriesData.push(s));

                if (allSeriesData.length > 0) {
                  widgetWithData.data = combineSeriesData(allSeriesData, {
                    metricValuesSource,
                    tableSources: editorCustomQuerySource
                      ? [...editorTableSources, editorCustomQuerySource]
                      : editorTableSources,
                  });
                } else {
                  widgetWithData.data = null;
                }
              } catch {
                widgetWithData.data = null;
              }
              return widgetWithData;
            }

            // Handle other widget types with queries
            // Use server-side query building for secure data fetching
            try {
              // Check if custom query has query text inside its config
              const customQuerySource = editorDataSources.find((ds) => ds.type === 'custom_query');
              const hasValidDataSource = 
                (editorHasCustomQuery && customQuerySource?.config?.query) ||
                editorTableSources.length > 0;

              if (hasValidDataSource) {
                // Config structure is already correct - dataSources array with config inside
                const sourceConfig = widget.config;

                const response = await fetchWidgetData(
                  widget.type,
                  sourceConfig,
                  {}, // No filters in preview mode
                  true, // In edit mode - apply LIMIT 1000
                  PREVIEW_ROW_LIMIT,
                );
                if (response.status === 'success' && response.data?.rows?.length > 0) {
                  // Limit rows to preview limit
                  const limitedData = {
                    ...response.data,
                    rows: response.data.rows.slice(0, PREVIEW_ROW_LIMIT),
                  };
                  widgetWithData.data = convertToWidgetData(
                    limitedData,
                    widget.type,
                    widget.config,
                    t('dashboard.widgets.noData'),
                  );
                } else {
                  widgetWithData.data = null;
                }
              } else {
                widgetWithData.data = null;
              }
            } catch {
              widgetWithData.data = null;
            }

            return widgetWithData;
          })),
          fetchStatusColorMap(),
        ]);

        const widgetsWithData = statusColorMap
          ? widgetsWithDataRaw.map(w =>
              w.data
                ? { ...w, data: applySeverityColors(w.data, w.type, w.config, statusColorMap) }
                : w,
            )
          : widgetsWithDataRaw;

        setWidgetsWithPreviewData(widgetsWithData);
      } else {
        setWidgetsWithPreviewData([]);
      }
    } catch {
      // Failed to load preview data
      setWidgetsWithPreviewData([]);
    } finally {
      setIsLoadingPreview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData.widgets, entityType]);

  // Load preview data when widgets are first loaded, when widgets are added/deleted, or when manually triggered
  useEffect(() => {
    if (dashboardData.widgets.length > 0) {
      loadPreviewData();
    } else {
      setWidgetsWithPreviewData([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData.widgets.length, previewTrigger]); // Trigger on count change OR manual trigger

  // Save widget handler - trigger immediate reload for responsive UX
  const handleSaveWidgetWithPreview = (widgetData) => {
    handleSaveWidget(widgetData);
    // Increment trigger to force preview reload via useEffect
    setPreviewTrigger((prev) => prev + 1);
  };

  // Build breadcrumb items based on mode
  const breadcrumbItems = isCreating
    ? [{ label: t('reports.pageTitle'), path: '/' }, { label: t('reports.createReport') }]
    : [{ label: t('reports.pageTitle'), path: '/' }, { label: dashboardData.name || t('reports.editReport') }];

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <Breadcrumbs items={breadcrumbItems} />

      {/* Header with Report Name */}
      <Header
        subtitle={!isCreating && dashboardData.name ? dashboardData.name : undefined}
        subtitleEditable={!isCreating && !!dashboardData.name}
        onSubtitleEdit={handleEditName}
        editAriaLabel={t('reports.editReportDetails')}
      >
        {t('reports.pageTitle')}
      </Header>

      {/* Error Display - only show banner when modal is NOT open */}
      {error && !showDetailsModal && (
        <div className={styles.errorBanner}>
          <span className={styles.errorIcon}>⚠️</span>
          <span className={styles.errorMessage}>{error}</span>
        </div>
      )}

      {/* Dependency Warning - shown when editing existing reports with dependencies */}
      {!isCreating && (
        <ReportDependencyWarning
          savedReportsCount={savedReportsCount}
          scheduledReportsCount={scheduledReportsCount}
          savedReports={savedReports}
          scheduledReports={scheduledReports}
        />
      )}

      {/* Main Content */}
      <div className={styles.scrollableContent}>
        {/* Required Filters Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('dashboard.requiredFilters.title')}</h2>
          </div>
          <div className={styles.sectionBody}>
            <FilterMultiSelect requiredFilters={dashboardData.requiredFilters} onChange={handleRequiredFiltersChange} />
          </div>
        </div>

        {/* Layout Section */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>{t('dashboard.layout')}</h2>
            <Button variant="primary" onClick={handleAddWidget} size="small" className={styles.addWidgetButton}>
              {t('dashboard.addWidget')}
            </Button>
          </div>
          <div className={styles.sectionBody}>
            {dashboardData.widgets.length === 0 ? (
              <div className={styles.emptyLayoutState}>
                <h4 className={styles.emptyTitle}>{t('dashboard.noWidgets')}</h4>
                <p className={styles.emptyDescription}>{t('dashboard.addFirst')}</p>
                <Button variant="primary" onClick={handleAddWidget} className={styles.addWidgetButton}>
                  {t('dashboard.addFirstWidget')}
                </Button>
              </div>
            ) : (
              <div className={styles.layoutContainer}>
                {isLoadingPreview && <div className={styles.loadingPreview}>{t('dashboard.loadingPreview')}</div>}
                <GridOverlay
                  cols={4}
                  rowHeight={GRID_CONFIG.rowHeight}
                  margin={GRID_CONFIG.margin}
                  containerPadding={GRID_CONFIG.containerPadding}
                  defaultWidgetHeight={6}
                  gridRef={gridRef}
                  layouts={layouts}
                />
                <ResponsiveGridLayout
                  ref={gridRef}
                  className={styles.layout}
                  layouts={layouts}
                  onLayoutChange={handleLayoutChange}
                  breakpoints={GRID_CONFIG.breakpoints}
                  cols={GRID_CONFIG.cols}
                  rowHeight={GRID_CONFIG.rowHeight}
                  margin={GRID_CONFIG.margin}
                  containerPadding={GRID_CONFIG.containerPadding}
                  isDraggable
                  isResizable
                  draggableHandle=".drag-handle"
                  compactType={null}
                  preventCollision
                >
                  {dashboardData.widgets.map((widget) => {
                    // Use preview data if available, otherwise use placeholder
                    const widgetToRender = widgetsWithPreviewData.find((w) => w.id === widget.id) || widget;
                    return (
                      <div key={String(widget.id)} className={styles.widgetContainer}>
                        <WidgetRenderer
                          widget={widgetToRender}
                          dashboardArgs={{}}
                          dashboardFilters={{}} // Empty filters in preview mode
                          isEditing
                          onEdit={handleEditWidget}
                          onDelete={handleDeleteWidget}
                        />
                      </div>
                    );
                  })}
                </ResponsiveGridLayout>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer with Action Buttons */}
      <div className={styles.footer}>
        <div className={styles.footerLeft} />
        <div className={styles.footerRight}>
          <Button variant="outline" onClick={onCancel} disabled={saving} className={styles.cancelButton}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            loading={saving && pendingSaveAction !== 'publish'}
            className={styles.saveButton}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveAndPublish}
            disabled={saving}
            loading={saving && pendingSaveAction === 'publish'}
          >
            {t('dashboard.saveAndPublish')}
          </Button>
        </div>
      </div>

      {/* Widget Wizard */}
      {showWidgetEditor && (
        <WidgetWizard
          widget={editingWidget}
          onSave={handleSaveWidgetWithPreview}
          onCancel={() => {
            setShowWidgetEditor(false);
            setEditingWidget(null);
          }}
        />
      )}

      {/* Report Details Modal */}
      <ReportDetailsModal
        isOpen={showDetailsModal}
        initialName={dashboardData.name}
        initialDescription={dashboardData.description}
        onSave={handleDetailsModalSave}
        onCancel={handleDetailsModalCancel}
        saving={saving && showDetailsModal}
        error={showDetailsModal ? error : null}
      />
    </div>
  );
}

DashboardEditor.propTypes = {
  dashboard: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    description: PropTypes.string,
    type: PropTypes.number,
    status: PropTypes.number,
    creator: PropTypes.string,
    widgets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title: PropTypes.string,
        type: PropTypes.string,
        config: PropTypes.shape({}),
        layout: PropTypes.shape({
          x: PropTypes.number,
          y: PropTypes.number,
          w: PropTypes.number,
          h: PropTypes.number,
        }),
      }),
    ),
    requiredFilters: PropTypes.arrayOf(PropTypes.string),
  }),
  isCreating: PropTypes.bool,
  saving: PropTypes.bool,
  saveSuccess: PropTypes.bool,
  error: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  entityType: PropTypes.string,
};

DashboardEditor.defaultProps = {
  dashboard: null,
  isCreating: false,
  saving: false,
  saveSuccess: false,
  error: null,
  entityType: 'Report',
};

export default DashboardEditor;
